import { createContext, useContext, useId, type CSSProperties, type ReactNode } from "react";
import { isoFaceHex, swatchHex } from "./color";
import { BOX, dotLattice, isoLayout, jugLayout, shapePath } from "./geometry";
import { ClueView, CubeView } from "./launch/glyphs";
import styles from "./PuzzleView.module.css";
import type {
  CellsGlyph,
  DotsGlyph,
  Glyph,
  IsoGlyph,
  JugGlyph,
  LinesGlyph,
  PuzzleColor,
  ShapeGlyph,
  StackGlyph,
  SwatchGlyph,
  TextGlyph,
} from "./types";
import { kindOf } from "./types";

const PUZZLE_COLORS = new Set<string>([
  "ink",
  "accent",
  "ember",
  "violet",
  "mint",
  "sky",
  "pink",
  "amber",
  "orange",
  "red",
  "blue",
  "purple",
  "yellow",
  "green",
  "muted",
]);

/** Semantic puzzle colour -> tokens.css custom property. Unknown colours fall back to ink. */
export function puzzleColor(color: PuzzleColor | string | undefined, fallback: PuzzleColor = "ink"): string {
  const name = color && PUZZLE_COLORS.has(color) ? color : fallback;
  return `var(--color-puzzle-${name})`;
}

/** Uncoloured (ink) text takes `--glyph-ink` when a cell sets it (a label on a solid fill), else the ink token. */
const textColor = (color: PuzzleColor | string | undefined) =>
  !color || color === "ink" ? "var(--glyph-ink, var(--color-puzzle-ink))" : puzzleColor(color);

const num = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export function Unsupported({ kind, detail }: { kind: string; detail?: string }) {
  return (
    <div className={styles.unsupported} role="note" data-testid="unsupported">
      <span>Unsupported: {kind}</span>
      {detail ? <span className={styles.unsupportedDetail}>{detail}</span> : null}
    </div>
  );
}

function Svg({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <svg
      className={styles.svg}
      viewBox={`0 0 ${BOX} ${BOX}`}
      preserveAspectRatio="xMidYMid meet"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {children}
    </svg>
  );
}

/**
 * How text glyphs size themselves: "fit" scales to the tile (the default), "line"
 * sets them at the tile-label type size on one line (option pills, where a
 * label such as "Only Nora" must read like a button, not shrink to fit a square).
 */
export const TextFit = createContext<"fit" | "line">("fit");

function TextView({ glyph }: { glyph: TextGlyph }) {
  const fit = useContext(TextFit);
  const value = String(glyph.value ?? "");
  const style = glyph.style ?? "number";
  if (fit === "line") {
    return (
      <div className={styles.textGlyph} data-style={style} data-line>
        <span style={{ color: textColor(glyph.color) }}>{value}</span>
      </div>
    );
  }
  const len = Math.max(1, [...value].length);
  // Size by the tile's short side (cqh) and by length along the tile's width (cqw).
  const byStyle: Record<string, [number, number]> = {
    number: [44, 118],
    letter: [46, 118],
    word: [24, 170],
    equation: [26, 176],
    label: [15, 150],
  };
  // A lone operator ("=", "+") in equation style is sized like a number, not like a whole equation.
  const [h, w] = (style === "equation" && len <= 2 ? byStyle.number : byStyle[style]) ?? byStyle.number!;
  const fontSize = `min(${h}cqh, ${Math.round((w / len) * 10) / 10}cqw)`;
  return (
    <div className={styles.textGlyph} data-style={style}>
      <span style={{ fontSize, color: textColor(glyph.color) }}>{value}</span>
    </div>
  );
}

/**
 * glyph.shape@1 in the 100×100 box. `radius` is the nominal half-size at scale 1:
 * 27 leaves the tile padding the boards show; board.scatter@1 passes 46 so the
 * shape fills its item square.
 */
export function ShapeView({ glyph, radius = 27 }: { glyph: ShapeGlyph; radius?: number }) {
  const rawId = useId();
  const clipId = `half-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const scale = Math.min(1, Math.max(0.1, num(glyph.scale, 1)));
  const path = shapePath(glyph.shape, radius * scale);
  if (!path) return <Unsupported kind={`shape.${String(glyph.shape)}`} />;
  // An ink shape follows `--glyph-ink` like ink text does (a dark arrow on a solid piece), else the ink token.
  const color = textColor(glyph.color);
  const fill = glyph.fill ?? "solid";
  const rotation = num(glyph.rotation, 0);
  const rule = path.evenOdd ? "evenodd" : "nonzero";
  const outline = (
    <path d={path.d} fill="none" stroke={color} strokeWidth={4} strokeLinejoin="round" fillRule={rule} />
  );
  return (
    <Svg label={`${fill} ${glyph.shape}`}>
      <g transform={`rotate(${rotation} 50 50)`}>
        <g transform={glyph.mirrored ? "matrix(-1 0 0 1 100 0)" : undefined}>
          {fill === "solid" ? <path d={path.d} fill={color} fillRule={rule} /> : null}
          {fill === "outline" ? outline : null}
          {fill === "half" ? (
            <>
              <clipPath id={clipId}>
                <rect x={0} y={0} width={50} height={100} />
              </clipPath>
              <path d={path.d} fill={color} fillRule={rule} clipPath={`url(#${clipId})`} />
              {outline}
            </>
          ) : null}
        </g>
      </g>
    </Svg>
  );
}

function DotsView({ glyph }: { glyph: DotsGlyph }) {
  const rows = Math.max(1, Math.trunc(num(glyph.rows, 3)));
  const cols = Math.max(1, Math.trunc(num(glyph.cols, 3)));
  const lattice = dotLattice(rows, cols);
  const on = arr<[number, number]>(glyph.on).filter((p) => Array.isArray(p) && p.length >= 2);
  const color = puzzleColor(glyph.color);
  return (
    <Svg label={`${on.length} dots on a ${rows} by ${cols} lattice`}>
      {on.map(([r, c], i) => {
        const [x, y] = lattice.at(Number(r), Number(c));
        return <circle key={`${r}-${c}-${i}`} cx={x} cy={y} r={lattice.radius} fill={color} />;
      })}
    </Svg>
  );
}

function LinesView({ glyph }: { glyph: LinesGlyph }) {
  const segments = arr<number[]>(glyph.segments).filter((s) => Array.isArray(s) && s.length >= 4);
  const color = puzzleColor(glyph.color);
  const m = (v: number) => 20 + Math.min(1, Math.max(0, Number(v))) * 60;
  return (
    <Svg label={`${segments.length} line segments`}>
      {segments.map((s, i) => (
        <line
          key={i}
          x1={m(s[0]!)}
          y1={m(s[1]!)}
          x2={m(s[2]!)}
          y2={m(s[3]!)}
          stroke={color}
          strokeWidth={4.5}
          strokeLinecap="round"
        />
      ))}
    </Svg>
  );
}

/**
 * A one-row cells glyph whose filled cells are one centred run of one colour (an odd
 * width 2k − 1 in 2n − 1 columns): a Tower of Hanoi disk. Returns the run, else null.
 */
export function diskRun(glyph: CellsGlyph): { from: number; to: number; color: string } | null {
  const rows = Math.trunc(num(glyph.rows, 0));
  const cols = Math.trunc(num(glyph.cols, 0));
  if (rows !== 1 || cols < 3 || cols % 2 === 0) return null;
  const fills = arr<{ at: [number, number]; color: PuzzleColor }>(glyph.fills).filter((f) => Array.isArray(f?.at));
  if (fills.length === 0) return null;
  const color = fills[0]!.color;
  const at = fills.map((f) => Number(f.at[1])).sort((a, b) => a - b);
  if (fills.some((f) => f.color !== color || Number(f.at[0]) !== 0) || new Set(at).size !== at.length) return null;
  const from = at[0]!;
  const to = at[at.length - 1]!;
  if (to - from + 1 !== at.length || from !== cols - 1 - to) return null;
  return { from, to, color };
}

function CellsView({ glyph }: { glyph: CellsGlyph }) {
  const rows = Math.max(1, Math.trunc(num(glyph.rows, 3)));
  const cols = Math.max(1, Math.trunc(num(glyph.cols, 3)));
  const disk = diskRun(glyph);
  if (disk) {
    // Drawn as one rounded bar across the full width, so disks of every size line up on their peg.
    const unit = 92 / cols;
    const x = 4 + disk.from * unit;
    const w = (disk.to - disk.from + 1) * unit;
    return (
      <Svg label={`bar ${disk.to - disk.from + 1} of ${cols} wide`}>
        <rect x={x} y={31} width={w} height={38} rx={14} style={{ fill: puzzleColor(disk.color) }} data-disk={disk.to - disk.from + 1} />
      </Svg>
    );
  }
  const span = 66;
  const cell = span / Math.max(rows, cols);
  const gap = cell * 0.12;
  const x0 = 50 - (cols * cell) / 2;
  const y0 = 50 - (rows * cell) / 2;
  const fills = new Map<string, string>();
  for (const f of arr<{ at: [number, number]; color: PuzzleColor }>(glyph.fills)) {
    if (Array.isArray(f?.at)) fills.set(`${f.at[0]},${f.at[1]}`, puzzleColor(f.color));
  }
  const rects: ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const color = fills.get(`${r},${c}`);
      rects.push(
        <rect
          key={`${r}-${c}`}
          x={x0 + c * cell + gap / 2}
          y={y0 + r * cell + gap / 2}
          width={cell - gap}
          height={cell - gap}
          rx={cell * 0.2}
          style={{ fill: color ?? "var(--color-surface-bubble)" }}
        />,
      );
    }
  }
  return <Svg label={`${fills.size} filled cells on a ${rows} by ${cols} grid`}>{rects}</Svg>;
}

function IsoView({ glyph }: { glyph: IsoGlyph }) {
  const { faces, unit } = isoLayout(arr<number[]>(glyph.voxels));
  // Default (and violet) stacks use the voxel tokens directly; other colours get
  // the same top/left/right lightness steps applied to their token colour.
  const tinted = Boolean(glyph.color && glyph.color !== "violet" && PUZZLE_COLORS.has(glyph.color));
  const fill = (face: "top" | "left" | "right") =>
    tinted ? isoFaceHex(glyph.color, face) : `var(--color-puzzle-voxel-${face})`;
  // Boards: 1.4px black strokes on a 26px voxel edge.
  const stroke = Math.max(0.5, unit * (1.4 / 26));
  return (
    <Svg label={`${faces.length ? new Set(faces.map((f) => f.voxel.join(","))).size : 0} visible cubes`}>
      {faces.map((f) => (
        <polygon
          key={f.key}
          data-face={f.face}
          points={f.points}
          style={{ fill: fill(f.face), stroke: "var(--color-bg)", strokeWidth: stroke, strokeLinejoin: "round" }}
        />
      ))}
    </Svg>
  );
}

/** glyph.swatch@1: a flat colour chip, OKLCH lightness-shifted from its token colour. */
function SwatchView({ glyph }: { glyph: SwatchGlyph }) {
  const color = swatchHex(glyph.color, glyph.tone);
  return (
    <Svg label={`${PUZZLE_COLORS.has(glyph.color) ? glyph.color : "ink"} swatch`}>
      <rect x={4} y={4} width={92} height={92} rx={20} style={{ fill: color }} data-swatch={color} />
    </Svg>
  );
}

/** The jugs a glyph draws as one group: a jug, or a (non-overlay) stack made only of jugs. Otherwise null. */
export function jugGroup(glyph: unknown): JugGlyph[] | null {
  if (kindOf(glyph) === "jug") return [glyph as JugGlyph];
  if (kindOf(glyph) !== "stack" || (glyph as StackGlyph).layout === "overlay") return null;
  const items = arr<Glyph>((glyph as StackGlyph).items);
  return items.length > 0 && items.every((g) => kindOf(g) === "jug") ? (items as JugGlyph[]) : null;
}

const jugText = (j: { capacity: number; fill: number }, unit: string) =>
  `${j.capacity} ${unit} jug, ${j.fill === 0 ? "empty" : j.fill >= j.capacity ? "full" : `holding ${j.fill} ${unit}`}`;

/**
 * glyph.jug@1, alone or as a group (a stack of jugs): every jug of the group at
 * one scale (same width, half the tallest jug's inner height; heights by
 * capacity; bottoms aligned), a tick every litre and a longer one every 5 L,
 * the current fill above each jug and "N L MAX" below it. `label` is not drawn.
 * `board` sizes the drawing by its width (a Water Jug board) instead of filling
 * a tile.
 */
export function JugsView({ jugs, board = false }: { jugs: readonly JugGlyph[]; board?: boolean }) {
  const rawId = useId();
  const idBase = `jug-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const layout = jugLayout(jugs);
  const unitOf = (i: number) => {
    const u = jugs[i]?.unit;
    return typeof u === "string" && u.trim() ? u.trim().slice(0, 4) : "L";
  };
  const label =
    layout.jugs.length === 1
      ? jugText(layout.jugs[0]!, unitOf(0))
      : `${layout.jugs.length} jugs: ${layout.jugs.map((j, i) => jugText(j, unitOf(i))).join("; ")}`;
  const { width, height } = layout.viewBox;
  const corner = 7;
  return (
    <svg
      className={board ? styles.jugSvg : styles.svg}
      // On a board every group draws at (at most) the same scale, so two jugs aren't blown up to fill the width.
      style={board ? { maxWidth: `${Math.round(width * 1.55)}px` } : undefined}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMax meet"
      role="img"
      aria-label={label}
      data-jugs={layout.jugs.length}
    >
      {layout.jugs.map((j, i) => {
        const cx = j.x + j.width / 2;
        const color = puzzleColor(jugs[i]?.color, "sky");
        const clip = `M${j.x},${j.top} h${j.width} v${j.bottom - j.top - corner} q0,${corner} ${-corner},${corner} h${-(j.width - 2 * corner)} q${-corner},0 ${-corner},${-corner} Z`;
        // The outline is open at the top: a vessel, not a box.
        const body = `M${j.x},${j.top} V${j.bottom - corner} q0,${corner} ${corner},${corner} h${j.width - 2 * corner} q${corner},0 ${corner},${-corner} V${j.top}`;
        const clipId = `${idBase}-${i}`;
        return (
          <g key={i} data-jug={j.capacity} data-fill={j.fill}>
            {j.water > 0 ? (
              <>
                <clipPath id={clipId}>
                  <path d={clip} />
                </clipPath>
                <rect
                  className={styles.jugWater}
                  x={j.x}
                  y={j.bottom - j.water}
                  width={j.width}
                  height={j.water}
                  clipPath={`url(#${clipId})`}
                  style={{ fill: color }}
                />
              </>
            ) : null}
            {j.ticks.map((t) => (
              <line
                key={t.y}
                className={styles.jugTick}
                x1={j.x}
                x2={j.x + (t.long ? 13 : 7)}
                y1={t.y}
                y2={t.y}
                data-long={t.long || undefined}
              />
            ))}
            <path className={styles.jugBody} d={body} />
            <text className={styles.jugFill} x={cx} y={j.top - 6} textAnchor="middle">
              {`${j.fill} ${unitOf(i)}`}
            </text>
            <text className={styles.jugMax} x={cx} y={j.bottom + 13} textAnchor="middle">
              {`${j.capacity} ${unitOf(i)} MAX`}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const OPERATORS = new Set(["+", "-", "−", "×", "x", "÷", "/", "=", ":", "<", ">", "≠"]);
/** A one-character operator in a row stack ("▲ + ●"): it takes less room than the terms around it. */
const isOperator = (g: unknown) => kindOf(g) === "text" && OPERATORS.has(String((g as TextGlyph).value ?? "").trim());

/** An overlay of a disk (see `diskRun`) and one short text: a numbered Tower of Hanoi disk. */
export function diskLabel(items: readonly Glyph[]): { cells: CellsGlyph; run: NonNullable<ReturnType<typeof diskRun>>; text: string } | null {
  if (items.length !== 2 || kindOf(items[0]) !== "cells" || kindOf(items[1]) !== "text") return null;
  const run = diskRun(items[0] as CellsGlyph);
  const text = String((items[1] as TextGlyph).value ?? "").trim();
  return run && text.length > 0 && text.length <= 3 ? { cells: items[0] as CellsGlyph, run, text } : null;
}

/** A disk glyph on its own or with its number (Tower of Hanoi). */
export function isDiskGlyph(g: unknown): boolean {
  if (kindOf(g) === "cells") return diskRun(g as CellsGlyph) !== null;
  return kindOf(g) === "stack" && (g as StackGlyph).layout === "overlay" && diskLabel(arr<Glyph>((g as StackGlyph).items)) !== null;
}

const isShape = (g: unknown, shape?: string): g is ShapeGlyph =>
  kindOf(g) === "shape" && (shape === undefined || (g as ShapeGlyph).shape === shape);

/**
 * Code Breaker feedback pegs: a row stack of 2–6 circles that are each solid accent
 * (right digit, right place), outline ink (right digit, wrong place) or solid muted
 * (no score). Drawn as a compact cluster beside the guess, like the boards (L8).
 */
export function isPegStack(glyph: unknown): boolean {
  if (kindOf(glyph) !== "stack" || ((glyph as StackGlyph).layout ?? "row") !== "row") return false;
  const items = arr<Glyph>((glyph as StackGlyph).items);
  if (items.length < 2 || items.length > 6) return false;
  return items.every((g) => {
    if (!isShape(g, "circle")) return false;
    const fill = g.fill ?? "solid";
    const color = g.color ?? "ink";
    return (color === "accent" && fill === "solid") || (color === "ink" && fill === "outline") || (color === "muted" && fill === "solid");
  });
}

/**
 * Word Clue squares: a column stack of a letter and a small mark (a shape at scale
 * ≤ 0.6) or `empty`. The letter keeps the whole square; the mark sits in the corner.
 */
function markedLetter(items: Glyph[], layout: string): { letter: Glyph; mark: ShapeGlyph | null } | null {
  if (layout !== "column" || items.length !== 2 || kindOf(items[0]) !== "text") return null;
  const mark = items[1];
  if (kindOf(mark) === "empty") return { letter: items[0]!, mark: null };
  if (isShape(mark) && num(mark.scale, 1) <= 0.6) return { letter: items[0]!, mark };
  return null;
}

function StackView({ glyph, depth }: { glyph: StackGlyph; depth: number }) {
  const fit = useContext(TextFit);
  const items = arr<Glyph>(glyph.items);
  const layout = glyph.layout ?? "row";
  const jugs = jugGroup(glyph);
  if (jugs) return <JugsView jugs={jugs} />;
  if (isPegStack(glyph)) {
    const shapes = items as ShapeGlyph[];
    const right = shapes.filter((g) => g.color === "accent").length;
    const wrong = shapes.filter((g) => (g.fill ?? "solid") === "outline").length;
    return (
      <div
        className={styles.pegs}
        role="img"
        aria-label={`${right} right place, ${wrong} wrong place`}
        style={{ "--peg-cols": items.length > 2 ? Math.ceil(items.length / 2) : items.length } as CSSProperties}
      >
        {shapes.map((g, i) => (
          <div key={i} className={styles.peg}>
            <ShapeView glyph={{ ...g, scale: 1 }} radius={40} />
          </div>
        ))}
      </div>
    );
  }
  const marked = markedLetter(items, layout);
  if (marked) {
    return (
      <div className={styles.marked}>
        <div className={styles.markedLetter}>
          <GlyphView glyph={marked.letter} depth={depth + 1} />
        </div>
        {marked.mark ? (
          <div className={styles.markedMark}>
            <ShapeView glyph={{ ...marked.mark, scale: 1 }} radius={40} />
          </div>
        ) : null}
      </div>
    );
  }
  if (layout === "overlay") {
    const disk = diskLabel(items);
    if (disk) {
      // A Tower of Hanoi disk with its number: the number sits on the bar in dark ink.
      const unit = 92 / Math.max(1, Math.trunc(num(disk.cells.cols, 1)));
      const x = 4 + disk.run.from * unit;
      const w = (disk.run.to - disk.run.from + 1) * unit;
      return (
        <Svg label={`disk ${disk.text}`}>
          <rect x={x} y={31} width={w} height={38} rx={14} style={{ fill: puzzleColor(disk.run.color) }} data-disk={disk.run.to - disk.run.from + 1} />
          <text x={50} y={50} textAnchor="middle" dominantBaseline="central" className={styles.diskNumber}>
            {disk.text}
          </text>
        </Svg>
      );
    }
    return (
      <div className={styles.stackOverlay}>
        {items.map((g, i) => (
          <div key={i} className={styles.stackLayer}>
            <GlyphView glyph={g} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  // "▲ + ●" (Symbol Equation, Balance Scale): the terms fill their share of the tile.
  const expression = items.some(isOperator);
  return (
    <div className={styles.stack} data-layout={layout === "column" ? "column" : "row"} data-line={fit === "line" || undefined}>
      {items.map((g, i) => (
        <div
          key={i}
          className={styles.stackItem}
          data-kind={kindOf(g)}
          data-op={isOperator(g) || undefined}
          style={{ "--n": items.length } as CSSProperties}
        >
          {expression && isShape(g) ? <ShapeView glyph={g} radius={42} /> : <GlyphView glyph={g} depth={depth + 1} />}
        </div>
      ))}
    </div>
  );
}

/**
 * Draws one glyph so it fills its (square or wide) container. Never throws for
 * unknown kinds; nesting is capped to keep hostile specs from recursing forever.
 */
export function GlyphView({ glyph, depth = 0 }: { glyph: Glyph | null | undefined; depth?: number }) {
  if (depth > 6) return <Unsupported kind="stack" detail="nested too deeply" />;
  if (!glyph || typeof glyph !== "object") return <Unsupported kind={kindOf(glyph)} />;
  switch (glyph.kind) {
    case "text":
      return <TextView glyph={glyph} />;
    case "shape":
      return <ShapeView glyph={glyph} />;
    case "dots":
      return <DotsView glyph={glyph} />;
    case "lines":
      return <LinesView glyph={glyph} />;
    case "cells":
      return <CellsView glyph={glyph} />;
    case "iso":
      return <IsoView glyph={glyph} />;
    case "stack":
      return <StackView glyph={glyph} depth={depth} />;
    case "swatch":
      return <SwatchView glyph={glyph} />;
    case "jug":
      return <JugsView jugs={[glyph]} />;
    case "slot":
      return <span className={styles.slotMark} aria-label="missing tile">?</span>;
    case "empty":
      return null;
    case "cube":
      return <CubeView glyph={glyph} />;
    case "clue":
      return <ClueView glyph={glyph} />;
    default:
      return <Unsupported kind={kindOf(glyph)} />;
  }
}

export function isSlot(glyph: unknown): boolean {
  return kindOf(glyph) === "slot";
}
